import { Injectable } from '@angular/core';
import packageInfo from '../../../../package.json';
import { ApplicationConfiguration } from '../types/application-configuration.type';
import { ApiService } from './api/api.service';
import { LoggingService } from './logging.service';
import { PopupService } from './popup.service';

@Injectable({ providedIn: 'root' })
export class AppConfigurationService {
  get applicationInsightsConnectionString(): string {
    return this.appConfiguration?.applicationInsightsConnectionString;
  }

  get isOnPremise(): boolean {
    return this.appConfiguration?.isOnPremise ?? false;
  }

  get azureApplicationId(): string {
    return this.appConfiguration?.azureApplicationId;
  }

  get azureAuthorityTenant(): string {
    return this.appConfiguration?.azureAuthorityTenant;
  }

  get microsoftLoginEnabled(): boolean {
    return this.appConfiguration?.microsoftLoginEnabled ?? true;
  }

  get oidcEnabled(): boolean {
    return this.appConfiguration?.oidcEnabled ?? false;
  }

  get oidcButtonLabel(): string {
    return this.appConfiguration?.oidcButtonLabel ?? 'Sign in with SSO';
  }

  get noOrganizationExists(): boolean {
    return this.appConfiguration?.noOrganizationExists ?? false;
  }

  get clientVersion(): string {
    return this.versions?.client;
  }

  get serverVersion(): string {
    return this.versions?.server;
  }

  get licenseExpiration(): Date {
    return this.appConfiguration?.licenseExpiration;
  }

  get licenseExpirationWarningDays(): number {
    return this.appConfiguration?.licenseExpirationWarningDays;
  }

  private appConfiguration: ApplicationConfiguration;
  private versions: {
    client: string;
    server: string;
  };

  static initialize(apiService: ApiService, appConfigurationService: AppConfigurationService, loggingService: LoggingService, popupService: PopupService): () => Promise<any> {
    return async () => {
      try {
        appConfigurationService.versions = { client: packageInfo.version, server: null };
        const versions = await apiService.getVersion();
        appConfigurationService.versions.server = versions.serverVersion.split('+')[0]; // remove build metadata
        appConfigurationService.appConfiguration = await apiService.getApplicationConfiguration();
      } catch (error) {
        await popupService.showInfo('Failed to load application configuration.', 'Server not responding', 'Try again');
        window.location.reload();
        return;
      }

      const applicationInsightsConnectionString = appConfigurationService.appConfiguration?.applicationInsightsConnectionString;
      loggingService.setupApplicationInsights(applicationInsightsConnectionString, appConfigurationService.serverVersion);
    };
  }
}
